import styles from './InsuranceTool.module.scss';
import * as React from 'react';
import moment from 'moment-timezone';
import { ComboBox, DatePicker, Label, MessageBar, MessageBarType, Pivot, PivotItem, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import { coverage, CoverageErrorResponse, CoverageResponse, Patient } from '../../../api';
import { ApiResponse } from 'apisauce';
import { HealAPI } from '../../../healApi';
import { AccountResponse } from '../../../api/accounts.types';
import { formatDate } from '../../../util/Date';
import { PageHeader } from '../../core/PageHeader';
import { PatientSelector } from '../../core/PatientSelector';
import { onPageDidMount, onPageWillUnmount } from '../../../redux/core';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/types';
import { useQueryString } from '../../../util/hooks';
import { InfoMessage } from '../../core/InfoMessage';

export const InsuranceTool = () => {
  const [insuranceId, setInsuranceId] = useState('');
  const [memberId, setMemberId] = useState('');
  const [memberFirstName, setMemberFirstName] = useState('');
  const [memberLastName, setMemberLastName] = useState('');
  const [memberDateOfBirth, setMemberDateOfBirth] = useState<Date>();
  const [memberZipCode, setMemberZipCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [response, setResponse] = useState<CoverageResponse>();
  const [error, setError] = useState<CoverageErrorResponse>();
  const [patientId, setPatientId] = useQueryString('pid', '');

  const [view, setView] = useQueryString('view', 'plan');

  const dispatch = useDispatch();
  const payers = useSelector((state: RootState) => state.visits.visitConfig.data?.payers || []);
  const payerList = payers.map((payer) => ({ text: payer.longName, key: payer.id }));

  useEffect(() => {
    dispatch(onPageDidMount('InsuranceTool'));
    return () => {
      dispatch(onPageWillUnmount('InsuranceTool'));
    };
  }, [dispatch]);

  const onPatientChange = (patient: Patient) => {
    if (patient) {
      HealAPI.fetchAccountInfo(patient.ownerAccountId).then((response: ApiResponse<AccountResponse>) => {
        if (response.ok && response.data) {
          const { addresses, signupZipcode } = response.data;
          const homeAddress = addresses.filter((a) => a.addressType === 'HOME')?.[0];
          const otherAddress = addresses.filter((a) => a.addressType === 'OTHER')?.[0];
          const placeOfServiceZipCode = homeAddress?.zipcode || otherAddress?.zipcode || signupZipcode;

          setInsuranceId(patient.insuranceId);
          setMemberId(patient.memberId);
          setMemberFirstName(patient.firstName);
          setMemberLastName(patient.lastName);
          setMemberDateOfBirth(new Date(patient.dateOfBirth));
          setMemberZipCode(placeOfServiceZipCode);
          setPatientId(patient.patientId);
          setResponse(undefined);
          setError(undefined);
        }
      });
    }
  };

  const canCheck = () => {
    if (insuranceId && memberId && memberDateOfBirth && memberZipCode && !checking) {
      return true;
    }
    return false;
  };

  const mapInsuranceIdToPayerId = (insuranceId: string) => {
    const payer = payers.find((p) => p.id === insuranceId);
    return payer?.eligiblePayerCode || 'unknown';
  };

  const check = () => {
    setChecking(true);
    setError(undefined);
    setResponse(undefined);
    coverage({
      coverageProvider: 'changehealthcare',
      healServiceCode: 'SICK_ADULT',
      payerId: mapInsuranceIdToPayerId(insuranceId),
      memberId: memberId,
      memberFirstName: memberFirstName || undefined,
      memberLastName: memberLastName || undefined,
      memberDOB: moment(memberDateOfBirth).format('YYYY-MM-DD'),
      placeOfServiceZipCode: memberZipCode,
      includeRawResponse: true,
    })
      .then(setResponse)
      .catch(setError)
      .finally(() => setChecking(false));
  };

  return (
    <div>
      <PageHeader title="Insurance Eligibility Checker" showShareButton />
      <Stack horizontal tokens={{ childrenGap: 20 }}>
        <Stack disableShrink root={{ style: { minWidth: 300 } }} tokens={{ childrenGap: 8 }}>
          <PatientSelector label="Patient (search for an existing patient)" onChange={onPatientChange} patientId={patientId} />
          <div className={styles.divider} />
          <TextField label="Place of Service ZIP Code" required onChange={(e, v) => setMemberZipCode(v!)} value={memberZipCode} disabled={checking} />
          <ComboBox
            label="Insurance Company"
            required
            selectedKey={insuranceId}
            allowFreeform
            autoComplete="on"
            onChange={(e, option) => option && setInsuranceId(option.key as string)}
            options={payerList}
            disabled={checking}
          />
          <TextField label="Member ID" required onChange={(e, v) => setMemberId(v!)} value={memberId} disabled={checking} />
          <TextField label="First Name" onChange={(e, v) => setMemberFirstName(v!)} value={memberFirstName} disabled={checking} />
          <TextField label="Last Name" onChange={(e, v) => setMemberLastName(v!)} value={memberLastName} disabled={checking} />
          <Stack>
            <Label required>Date of Birth</Label>
            <DatePicker
              onSelectDate={(v) => setMemberDateOfBirth(v!)}
              value={memberDateOfBirth}
              allowTextInput={true}
              disableAutoFocus={true}
              formatDate={formatDate}
              disabled={checking}
            />
            <PrimaryButton style={{ marginTop: 20, width: 180 }} text="Check" disabled={!canCheck()} onClick={() => check()} />
          </Stack>
        </Stack>
        <Stack grow={1} style={{ width: '70%' }}>
          <Pivot onLinkClick={(i) => setView(i?.props.itemKey)} selectedKey={view}>
            <PivotItem headerText="Eligibility" itemKey="plan" />
            <PivotItem headerText="Json" itemKey="json" />
            <PivotItem headerText="X12" itemKey="x12" />
          </Pivot>
          <Stack className={styles.details}>
            {!checking && !response && !error && (
              <InfoMessage>
                <div>Add the patient details and check insurance eligibility.</div>
              </InfoMessage>
            )}
            {view === 'plan' && response && <Eligibility info={response} />}
            {view === 'json' && response && (
              <div>
                <pre style={{ overflow: 'auto' }}>{JSON.stringify(response, undefined, 2)}</pre>
              </div>
            )}
            {view === 'x12' && response && response.rawResponse && <TextField multiline rows={30} value={response.rawResponse.x12} />}
            {checking && <Spinner size={SpinnerSize.large} />}
            {error && error.message && <MessageBar messageBarType={MessageBarType.error}>{error.message}</MessageBar>}
            {error && !error.message && (
              <div>
                <pre style={{ overflow: 'auto' }}>{JSON.stringify(error, undefined, 2)}</pre>
              </div>
            )}
          </Stack>
        </Stack>
      </Stack>
    </div>
  );
};

const Eligibility = ({ info }: { info: CoverageResponse }) => {
  return (
    <>
      <MessageBar messageBarType={info.insurancePlan.coverageStatus === '1' ? MessageBarType.success : MessageBarType.severeWarning}>
        {info.insurancePlan.coverageStatusLabel}
      </MessageBar>
      <Stack styles={{ root: { paddingTop: 20 } }} tokens={{ childrenGap: 20 }}>
        <SubscriberInfo info={info} />
        <PlanInfo info={info} />
      </Stack>
    </>
  );
};

const PlanInfo = ({ info }: { info: CoverageResponse }) => {
  return (
    <Stack>
      <h3>Insurance</h3>
      <div>{info.payer.name}</div>
      <div>{info.insurancePlan.groupName}</div>
      <div>{info.insurancePlan.planName}</div>
      <div>Plan start: {info.insurancePlan.planStart || 'Not provided'}</div>
      <div>Plan end: {info.insurancePlan.planEnd || 'Not provided'}</div>
      {info.insurancePlan.comments && info.insurancePlan.comments.length > 0 && (
        <>
          <h4>Comments</h4>
          {info.insurancePlan.comments.map((c, i) => (
            <div key={i}>{c}</div>
          ))}
        </>
      )}
    </Stack>
  );
};

const SubscriberInfo = ({ info }: { info: CoverageResponse }) => {
  const [checkingCoverage, setCheckingCoverage] = useState(false);
  const [hasCoverage, setHasCoverage] = useState(false);
  const [coverageMessage, setCoverageMessage] = useState<any>();

  useEffect(() => {
    if (info.subscriber?.address?.zip) {
      const zipcode = info.subscriber.address.zip.slice(0, 5);
      setCheckingCoverage(true);
      HealAPI.checkZipcodeCoverage(zipcode)
        .then((r) => r.data)
        .then((data) => {
          const { covered, supportHouseCallServices, marketName, marketCode, serviceabilityDelayInSeconds, partialZipcodeCoverage } = data;
          setHasCoverage(covered);
          if (!covered) {
            setCoverageMessage(`ZIP code ${zipcode} (from insurance info) is not in a Heal coverage area.`);
          } else {
            const message = `ZIP code ${zipcode} (from insurance info) is ${partialZipcodeCoverage ? 'partially' : 'fully'} covered for ${
              supportHouseCallServices ? 'HOUSE and TELEHEALTH' : 'TELEHEALTH'
            } services. Market is ${marketName} (${marketCode}).`;

            const minBooking = serviceabilityDelayInSeconds === 0 ? 'SAME DAY' : `${serviceabilityDelayInSeconds / (60 * 60 * 24)} HOURS`;
            if (partialZipcodeCoverage) {
              setCoverageMessage(`Partial Coverage (${minBooking}). ${message}`);
            } else {
              setCoverageMessage(`Covered (${minBooking}). ${message}`);
            }
          }
        })
        .finally(() => setCheckingCoverage(false));
    }
  }, [info.subscriber.address]);

  return (
    <Stack horizontal tokens={{ childrenGap: 20 }}>
      <Stack styles={{ root: { minWidth: 200 } }}>
        <h3>Subscriber</h3>
        <div>{`${info.subscriber.firstName} ${info.subscriber.middleName || ''} ${info.subscriber.lastName}`}</div>
        <div>Date of Birth: {info.subscriber.dob}</div>
        <div>Sex: {info.subscriber.gender}</div>
        <div>Member ID: {info.subscriber.memberId}</div>
        {info.subscriber.groupId && <div>Group ID: {info.subscriber.groupId}</div>}
      </Stack>
      {info.subscriber.address && (
        <Stack tokens={{ childrenGap: 10 }}>
          <Stack>
            <h3>Address</h3>
            <div>{info.subscriber.address.streetLine_1}</div>
            <div>{info.subscriber.address.streetLine_2}</div>
            <div>{`${info.subscriber.address.city}, ${info.subscriber.address.state}, ${info.subscriber.address.zip}`}</div>
          </Stack>
          <Stack>
            {checkingCoverage && <Spinner />}
            {!checkingCoverage && <MessageBar messageBarType={hasCoverage ? MessageBarType.success : MessageBarType.error}>{coverageMessage}</MessageBar>}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
